import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@wbk/ui';
import { ContentfulRichText } from '@wbk/contentful';
import { type ReferralCard, type WebSection } from '@wbk/contentful/api';

import { useScrollLock } from '@/hooks/use-scroll-lock';

interface TermsModalProps {
  terms?: WebSection | ({ __typename: 'AppReferralCard' } & ReferralCard) | null;
  benefits?: WebSection | ({ __typename: 'AppReferralCard' } & ReferralCard) | null;
  isOpen: boolean;
  onClose: () => void;
  onAgree: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ terms, benefits, isOpen, onClose, onAgree }) => {
  const { t } = useTranslation('common');

  const { lock, unlock } = useScrollLock({ autoLock: false, widthReflow: true });

  useEffect(() => {
    if (isOpen) {
      lock();
    } else {
      unlock();
    }

    return () => unlock();
  }, [isOpen, lock, unlock]);

  if (!isOpen) return null;

  const isWebSection = (item: unknown): item is WebSection =>
    typeof item === 'object' &&
    item !== null &&
    (item as { __typename?: string }).__typename === 'WebSection';

  const termsContent = isWebSection(terms) ? terms.collapsableDescription : undefined;
  const benefitsContent = isWebSection(benefits) ? benefits.collapsableDescription : undefined;

  return (
    <div className='fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm'>
      <div className='flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-white/10 bg-[#1E1E1E] shadow-2xl'>
        <div className='flex items-center justify-between border-b border-white/10 p-6'>
          <h3 className='text-xl font-bold text-white'>
            {(isWebSection(terms) && terms.title) || t('terms_and_conditions')}
          </h3>
          <button
            onClick={onClose}
            className='text-gray-400 transition-colors hover:text-white'
            type='button'
          >
            <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        <div className='custom-scrollbar flex-grow space-y-8 overflow-y-auto p-6 text-sm leading-relaxed text-gray-300'>
          {termsContent && <ContentfulRichText description={termsContent} />}
          <div className='space-y-3 border-t border-white/10 pt-6'>
            <h3 className='text-xl font-bold text-white'>
              {isWebSection(benefits) ? benefits.title : ''}
            </h3>
            <h3 className='text-lg font-semibold text-gray-300'>
              {isWebSection(benefits) ? benefits.subtitle : ''}
            </h3>
          </div>
          {benefitsContent && <ContentfulRichText description={benefitsContent} />}
        </div>

        <div className='flex flex-col justify-between gap-3 rounded-b-2xl border-t border-white/10 bg-white/5 p-6 sm:flex-row sm:items-center'>
          <div />
          <div className='flex flex-col gap-3 sm:flex-row'>
            <Button theme='transparent' onClick={onClose} className='rounded-full px-6'>
              {t('sponsor_form.buttons.cancel')}
            </Button>
            <button
              type='button'
              onClick={onAgree}
              className='rounded-full bg-white px-6 py-2 text-center font-semibold text-black transition-colors hover:bg-gray-200'
            >
              {t('sponsor_form.buttons.agree')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
